import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import GET_COMPONENT_SELECTOR_PRODUCT_FILTERS from 'api/queries/GET_COMPONENT_SELECTOR_PRODUCT_FILTERS';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import Accordion from 'layout/atoms/Accordion/Accordion';
import Button from 'layout/atoms/Button/Button';
import Checkbox from 'layout/atoms/Checkbox/Checkbox';
import Loader from 'layout/atoms/Loader/Loader';
import { setFilter } from 'store/componentsSlice';
import styles from './ProductParameterFiltersPanel.module.scss';
const filterGroups = [
    {
        filterName: 'functionIds',
        labelKey: 'functions',
        connectionName: 'productFunctions',
    },
    {
        filterName: 'sustainabilityIds',
        labelKey: 'sustainability',
        connectionName: 'productSustainabilities',
    },
    {
        filterName: 'certificateIds',
        labelKey: 'certificates',
        connectionName: 'productCertificates',
    },
    {
        filterName: 'continentIds',
        labelKey: 'continents',
        connectionName: 'productContinents',
    },
    {
        filterName: 'countryIds',
        labelKey: 'countries',
        connectionName: 'productCountries',
    },
    {
        filterName: 'eventIds',
        labelKey: 'events',
        connectionName: 'productEvents',
    },
    {
        filterName: 'mainCategoryIds',
        labelKey: 'main_categories',
        connectionName: 'productMainCategories',
    },
];
function ProductParameterFiltersPanel() {
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const filters = useSelector((state) => state.components.filters);
    const { data, loading, error } = useQuery(GET_COMPONENT_SELECTOR_PRODUCT_FILTERS, {
        variables: { first: 100 },
    });
    const [openedGroups, setOpenedGroups] = useState({
        functionIds: true,
        sustainabilityIds: false,
        certificateIds: false,
        continentIds: false,
        countryIds: false,
        eventIds: false,
        mainCategoryIds: false,
    });
    const selectedCount = filterGroups.reduce((sum, group) => { var _a; return sum + (((_a = filters[group.filterName]) === null || _a === void 0 ? void 0 : _a.length) || 0); }, 0);
    const optionsByGroup = useMemo(() => filterGroups.reduce((groups, group) => {
        var _a, _b;
        const edges = ((_b = (_a = data === null || data === void 0 ? void 0 : data.autocomplete) === null || _a === void 0 ? void 0 : _a[group.connectionName]) === null || _b === void 0 ? void 0 : _b.edges) || [];
        let options = edges
            .map((edge) => edge === null || edge === void 0 ? void 0 : edge.node)
            .filter((node) => (node === null || node === void 0 ? void 0 : node.id) && (node === null || node === void 0 ? void 0 : node.name));
        const selectedContinentIds = filters.continentIds || [];
        if (group.filterName === 'countryIds' && selectedContinentIds.length > 0) {
            options = options.filter((country) => { var _a; return selectedContinentIds.includes((_a = country.continent) === null || _a === void 0 ? void 0 : _a.id); });
        }
        return Object.assign(Object.assign({}, groups), { [group.filterName]: options });
    }, {}), [data, filters.continentIds]);
    const toggleGroup = (filterName) => {
        setOpenedGroups((current) => (Object.assign(Object.assign({}, current), { [filterName]: !current[filterName] })));
    };
    const handleOptionToggle = (filterName, optionId) => {
        var _a, _b, _c;
        const selectedIds = filters[filterName] || [];
        const isDeselecting = selectedIds.includes(optionId);
        const nextIds = isDeselecting
            ? selectedIds.filter((id) => id !== optionId)
            : [...selectedIds, optionId];
        dispatch(setFilter({ filterName, value: nextIds }));
        if (filterName === 'continentIds' && isDeselecting) {
            const allCountries = ((_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.autocomplete) === null || _a === void 0 ? void 0 : _a.productCountries) === null || _b === void 0 ? void 0 : _b.edges) === null || _c === void 0 ? void 0 : _c.map((e) => e === null || e === void 0 ? void 0 : e.node)) || [];
            const countryIdsFromContinent = allCountries
                .filter((c) => { var _a; return ((_a = c === null || c === void 0 ? void 0 : c.continent) === null || _a === void 0 ? void 0 : _a.id) === optionId; })
                .map((c) => c.id);
            if (countryIdsFromContinent.length > 0) {
                const selectedCountryIds = filters.countryIds || [];
                const nextCountryIds = selectedCountryIds.filter((id) => !countryIdsFromContinent.includes(id));
                if (nextCountryIds.length !== selectedCountryIds.length) {
                    dispatch(setFilter({ filterName: 'countryIds', value: nextCountryIds }));
                }
            }
        }
    };
    const handleClearFilters = () => {
        filterGroups.forEach((group) => {
            dispatch(setFilter({ filterName: group.filterName, value: [] }));
        });
    };
    return (React.createElement("aside", { className: styles.panel },
        React.createElement("div", { className: styles.header },
            React.createElement("h2", null, t('common:filters')),
            selectedCount > 0 && (React.createElement(Button, { size: "xs", theme: "ghost", onClick: handleClearFilters }, t('common:clear_filters')))),
        loading && React.createElement(Loader, null),
        error && React.createElement("p", { className: styles.state }, t('common:error')),
        !loading &&
            !error &&
            filterGroups.map((group) => {
                const options = optionsByGroup[group.filterName] || [];
                const selectedIds = filters[group.filterName] || [];
                const isOpened = openedGroups[group.filterName];
                return (React.createElement("section", { key: group.filterName, className: styles.group },
                    React.createElement(Accordion, { opened: isOpened, onClick: () => toggleGroup(group.filterName), header: React.createElement("div", { className: styles.groupHeader },
                            React.createElement("span", null, t(group.labelKey)),
                            React.createElement("span", { className: styles.groupMeta },
                                selectedIds.length > 0 && (React.createElement("span", { className: styles.counter }, selectedIds.length)),
                                React.createElement(ChevronDownSvg, { className: classNames(styles.chevron, {
                                        [styles.opened]: isOpened,
                                    }) }))) },
                        React.createElement("div", { className: styles.options },
                            options.length === 0 && React.createElement("p", { className: styles.state }, t('common:lack')),
                            options.map((option) => (React.createElement("div", { key: option.id, className: styles.option },
                                React.createElement(Checkbox, { size: "m", label: option.name, value: selectedIds.includes(option.id), onClick: () => handleOptionToggle(group.filterName, option.id) }))))))));
            })));
}
export default memo(ProductParameterFiltersPanel);
